import React, { FC, useCallback } from "react";

import { faDownload } from "@fortawesome/pro-solid-svg-icons/faDownload";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Tooltip from "antd/es/tooltip";
import { useIntl } from "react-intl";

import { EntityTypesSupportedColumnSelection } from "@mapmycustomers/shared/types/entity";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import { Button } from "@mapmycustomers/ui";

import DownloadListDataConfirmModal from "@app/component/ExportListDataConfirmModal";
import localSettings from "@app/config/LocalSettings";
import useDrillDownView from "@app/scene/dashboard/components/cards/common/DrillDownModal/util/useDrillDownView";
import useAnalytics from "@app/util/contexts/useAnalytics";

export interface ExportButtonProps {
  onExport?: () => void;
}

const ExportButton: FC<ExportButtonProps> = ({ onExport }) => {
  const analytics = useAnalytics();

  const intl = useIntl();
  const { entityType } = useDrillDownView();

  const [exportConfirmationVisible, showExportConfirmation, hideExportConfirmation] = useBoolean();

  const handleExportListData = useCallback(() => {
    if (localSettings.getDontShowExportListDataConfirmation(entityType!)) {
      analytics.clicked(["Export Data"]);
      onExport?.();
    } else {
      showExportConfirmation();
    }
  }, [analytics, entityType, onExport, showExportConfirmation]);

  const handleExportListDataConfirm = useCallback(
    (dontShowAgain: boolean) => {
      if (dontShowAgain) {
        localSettings.setDontShowExportListDataConfirmation(entityType!);
      }
      analytics.clicked(["Export Data"]);
      onExport?.();
    },
    [analytics, entityType, onExport]
  );

  return (
    <>
      <Tooltip
        placement="bottom"
        title={intl.formatMessage({
          id: "dashboard.drillDown.action.export",
          defaultMessage: "Export table data",
          description: "Export option in the DrillDown header on the Dashboard page",
        })}
      >
        <Button
          icon={<FontAwesomeIcon icon={faDownload} />}
          iconOnly
          onClick={handleExportListData}
          type="text"
        />
      </Tooltip>
      {exportConfirmationVisible && (
        <DownloadListDataConfirmModal
          entityType={entityType as EntityTypesSupportedColumnSelection}
          onConfirm={handleExportListDataConfirm}
          onHide={hideExportConfirmation}
        />
      )}
    </>
  );
};

export default ExportButton;
